(() => {
  if (customElements.get('native-gallery')) return;
  let activeGallery = null;

  class NativeGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.fullImage = this.querySelector('.native-gallery__full-image');
      this.status = this.querySelector('.native-gallery__status');
      this.previous = this.querySelector('.native-gallery__previous');
      this.next = this.querySelector('.native-gallery__next');
      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-gallery-image]');
        if (!opener || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(opener);
      }, options);
      this.querySelector('.native-gallery__close').addEventListener('click', () => this.close(), options);
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => {
        // A queued close event must not tear down a newly reopened dialog.
        if (!this.dialog.open) this.restore();
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, options);
      const stage = this.querySelector('.native-gallery__stage');
      stage.addEventListener('touchstart', (event) => {
        this.touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { ...options, passive: true });
      stage.addEventListener('touchend', (event) => {
        if (!this.touch || event.touches.length || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        this.touch = null;
        if ((window.visualViewport?.scale || 1) > 1) return;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.move(dx < 0 ? 1 : -1);
      }, { ...options, passive: true });
      stage.addEventListener('touchcancel', () => { this.touch = null; }, options);
      this.fullImage.addEventListener('error', () => {
        if (activeGallery !== this) return;
        this.status.textContent = `Image ${this.index + 1} of ${this.items.length}. Image could not load. Try the next image.`;
      }, options);
    }

    disconnectedCallback() {
      this.close(false);
      this.controller?.abort();
      this.controller = null;
    }

    open(opener) {
      activeGallery?.close();
      this.items = Array.from(this.querySelectorAll('[data-gallery-image]'));
      this.index = this.items.indexOf(opener);
      if (this.index < 0) return;
      this.opener = opener;
      this.scrollY = window.scrollY;
      this.savedBody = {};
      ['position', 'top', 'width', 'overflow'].forEach((key) => { this.savedBody[key] = document.body.style[key]; });
      Object.assign(document.body.style, { position: 'fixed', top: `-${this.scrollY}px`, width: '100%', overflow: 'hidden' });
      activeGallery = this;
      this.render();
      this.dialog.showModal();
    }

    move(direction) {
      this.index = (this.index + direction + this.items.length) % this.items.length;
      this.render();
    }

    render() {
      const item = this.items[this.index];
      this.fullImage.alt = item.dataset.alt;
      this.fullImage.src = item.href;
      this.status.textContent = `Image ${this.index + 1} of ${this.items.length}`;
      this.previous.hidden = this.next.hidden = this.items.length < 2;
    }

    close(returnFocus = true) {
      if (activeGallery !== this) return;
      this.returnFocus = returnFocus;
      this.dialog.close();
      this.restore();
    }

    restore() {
      if (activeGallery !== this) return;
      activeGallery = null;
      Object.assign(document.body.style, this.savedBody);
      window.scrollTo({ top: this.scrollY, behavior: 'instant' });
      if (this.returnFocus !== false && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.returnFocus = true;
      this.fullImage.removeAttribute('src');
    }
  }

  customElements.define('native-gallery', NativeGallery);
  // Section replacement and reordering invalidate the open image list.
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder', 'shopify:section:select'].forEach((name) => {
    document.addEventListener(name, () => activeGallery?.close());
  });
})();
